import React from 'react';
import { normalizeStatus, getStatusLabelValue } from '../../utils/statusUtils';
import { useLanguage } from '../../contexts/LanguageContext';
import useFormatCurrency from '../../hooks/useFormatCurrency';
import '../../styles/components/enhanced-table.css';

/**
 * Enhanced Table Component
 * Stellt sicher, dass Tabellen-Stile korrekt angezeigt werden
 */
const EnhancedTable = ({
  children,
  className = '',
  tableClassName = '',
  containerClassName = '',
  ...props
}) => {
  const tableClasses = ['table', 'table--hover', 'table--striped', className, tableClassName]
    .filter(Boolean)
    .join(' ');
  const containerClasses = ['table-container', containerClassName].filter(Boolean).join(' ');

  return (
    <div className={containerClasses}>
      <div className="et-scroll">
        <table className={tableClasses} {...props}>
          {children}
        </table>
      </div>
    </div>
  );
};

/**
 * Enhanced Table Header
 */
const EnhancedTableHeader = ({ children, className = '', ...props }) => {
  return (
    <thead
      className={`table__header ${className}`}
      {...props}
    >
      {children}
    </thead>
  );
};

/**
 * Enhanced Table Header Cell
 */
const EnhancedTableHeaderCell = ({ children, className = '', ...props }) => {
  return (
    <th
      className={`table__header-cell ${className}`}
      {...props}
    >
      {children}
    </th>
  );
};

/**
 * Enhanced Table Body
 */
const EnhancedTableBody = ({ children, className = '', ...props }) => {
  return (
    <tbody className={`table__body ${className}`} {...props}>
      {children}
    </tbody>
  );
};

/**
 * Enhanced Table Row
 */
const EnhancedTableRow = ({ children, className = '', isEven = false, ...props }) => {
  return (
    <tr
      className={`table__row ${className}`}
      {...props}
    >
      {children}
    </tr>
  );
};

/**
 * Enhanced Table Cell
 */
const EnhancedTableCell = ({
  children,
  className = '',
  align = 'left',
  isNumeric = false,
  isCurrency = false,
  ...props
}) => {
  const { isRTL } = useLanguage();
  const modifiers = [
    align === 'right' ? 'table__cell--right' : '',
    isNumeric && !isCurrency ? 'table__cell--mono' : '',
    isCurrency ? 'table__cell--currency' : ''
  ].filter(Boolean).join(' ');

  const content = isCurrency && isRTL
    ? <span className="ads-money rtl-amount">{children}</span>
    : isCurrency
      ? <span className="ads-money">{children}</span>
      : children;

  return (
    <td
      className={`table__cell ${modifiers} ${className}`}
      {...props}
    >
      {content}
    </td>
  );
};

/**
 * Loading State Component
 */
const TableLoading = ({ message }) => {
  const { t } = useLanguage();
  const text = message ?? t('tableCommon.loading');
  return (
    <div className="table-loading">
      <div className="spinner"></div>
      <span>{text}</span>
    </div>
  );
};

/**
 * Empty State Component
 */
const TableEmpty = ({
  icon = '📦',
  title,
  description
}) => {
  const { t } = useLanguage();
  return (
    <div className="table-empty">
      <div className="table-empty-icon">{icon}</div>
      <div className="table-empty-title">{title ?? t('tableCommon.emptyTitle')}</div>
      <div className="table-empty-description">{description ?? t('tableCommon.emptyDescription')}</div>
    </div>
  );
};

/**
 * Status Badge Component
 */
const StatusBadge = ({ status, children }) => {
  const getStatusClass = (status) => {
    switch (normalizeStatus(status)) {
      case 'completed':
      case 'terminé':
      case 'مكتمل':
      case 'true':
      case '1':
        return 'status-completed';
      case 'pending':
      case 'en attente':
      case 'في الانتظار':
      case 'processing':
      case 'en cours':
        return 'status-pending';
      case 'cancelled':
      case 'annulé':
      case 'ملغي':
      case 'false':
      case '0':
        return 'status-cancelled';
      default:
        return 'status-default';
    }
  };

  return (
    <span className={`status-badge ${getStatusClass(status)}`}>
      {children || getStatusLabelValue(status)}
    </span>
  );
};

/**
 * Action Button Component
 */
const ActionButton = ({
  onClick,
  children,
  variant = 'default',
  title,
  className = '',
  style: _ignoredStyle,
  ...props
}) => {
  const variantClass = variant === 'danger' ? 'et-action-btn--danger' : '';

  return (
    <button
      type="button"
      onClick={onClick}
      className={`et-action-btn ${variantClass} ${className}`.trim()}
      title={title}
      {...props}
    >
      {children}
    </button>
  );
};

/**
 * Table Summary Row Component
 */
const TableSummaryRow = ({
  data = [],
  columns = [],
  className = '',
  currentLanguage = 'fr',
  ...props
}) => {
  const { isRTL, t } = useLanguage();
  const formatCurrencyLocal = useFormatCurrency(currentLanguage);

  const calculateSum = (columnKey) => {
    return data.reduce((sum, row) => {
      const value = row[columnKey];
      if (typeof value === 'number') {
        return sum + value;
      }
      if (typeof value === 'string' && !isNaN(parseFloat(value))) {
        return sum + parseFloat(value);
      }
      return sum;
    }, 0);
  };

  return (
    <tr
      className={`table__summary-row et-summary-row ${className}`}
      {...props}
    >
      {columns.map((column, index) => {
        if (index === 0) {
          return (
            <td key={index} className="et-summary-label">
              {t('total')}
            </td>
          );
        }

        if (column.summarizable) {
          const sum = calculateSum(column.key);
          return (
            <td key={index} className={`et-summary-value${column.isCurrency ? ' table__cell--currency' : ''}`}>
              {column.isCurrency ? (
                <span className={isRTL ? 'ads-money rtl-amount' : 'ads-money'}>
                  {formatCurrencyLocal(sum)}
                </span>
              ) : (() => {
                let formatted = sum.toLocaleString('fr-MR', { numberingSystem: 'latn' });
                formatted = String(formatted).replace(/(\d)\/(\d{3})/g, '$1.$2');
                formatted = String(formatted).replace(/(\d)\/(\d)/g, '$1.$2');
                return formatted;
              })()}
            </td>
          );
        }

        return (
          <td key={index} className="et-summary-empty">
            -
          </td>
        );
      })}
    </tr>
  );
};

export {
  EnhancedTable,
  EnhancedTableHeader,
  EnhancedTableHeaderCell,
  EnhancedTableBody,
  EnhancedTableRow,
  EnhancedTableCell,
  TableLoading,
  TableEmpty,
  StatusBadge,
  ActionButton,
  TableSummaryRow
};

export default EnhancedTable;
